// Library imports
import React from 'react';
// Util imports
import { shortName } from '../../../../utils/strings';
import { formatOdd } from '../../../../utils/odds';
import { getStarRating, readableColor } from '../../utils';
import { imageLoadError } from '../../../../utils';
// Styled components
import { InnerContent, TeamLogoSmall } from '../styles';

const Prediction = ({ game, isLocked = false, unlockCredits = 1 }) => {
    const [showUnlockHint, setShowUnlockHint] = React.useState(false);

    if (isLocked) {
        return (
            <InnerContent fluid>
                <div
                    style={{
                        margin: '0.55rem',
                        padding: '1.05rem 1rem',
                        border: '1px dashed rgba(250,204,21,0.45)',
                        borderRadius: 12,
                        background: 'linear-gradient(160deg, rgba(113,63,18,0.18) 0%, rgba(15,23,42,0.58) 100%)',
                        color: '#fef3c7',
                        textAlign: 'center',
                        cursor: 'help',
                        transition: 'border-color 0.18s ease, background 0.18s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                    title="Use the Unlock button in the header to unlock this prediction with credits."
                    onMouseEnter={() => setShowUnlockHint(true)}
                    onMouseLeave={() => setShowUnlockHint(false)}
                >
                    <span
                        style={{
                            width: 30,
                            height: 30,
                            borderRadius: 999,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 8,
                            border: '1px solid rgba(250,204,21,0.5)',
                            background: 'rgba(250,204,21,0.14)'
                        }}
                    >
                        <i className="fas fa-lock" style={{ fontSize: '0.82rem', color: '#fef3c7' }} />
                    </span>
                    <p style={{ margin: 0, fontSize: '0.84rem', fontWeight: 800, color: '#fffbeb' }}>Prediction locked</p>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.74rem', color: '#fcd34d' }}>
                        Unlock cost: {unlockCredits} credit{unlockCredits > 1 ? 's' : ''}
                    </p>
                    <p
                        style={{
                            margin: '0.45rem 0 0',
                            fontSize: '0.72rem',
                            color: showUnlockHint ? '#ffffff' : 'rgba(255,251,235,0.88)',
                            opacity: showUnlockHint ? 1 : 0.84,
                            transition: 'opacity 0.18s ease, color 0.18s ease',
                        }}
                    >
                        Hover tip: use the Unlock button in the header to purchase with credits.
                    </p>
                </div>
            </InnerContent>
        );
    }

    if (!game?.prediction) {
        return (
            <InnerContent fluid>
                <div style={{ margin: '0.55rem', padding: '1rem 1.1rem', border: '1px dashed rgba(148,163,184,0.35)', borderRadius: 10, background: 'rgba(255,255,255,0.02)', color: '#94a3b8', textAlign: 'center' }}>
                    <i className="fas fa-calculator" style={{ fontSize: '1.4rem', marginBottom: 8, display: 'block' }} />
                    <p style={{ margin: 0, fontSize: '0.85rem' }}>No prediction data available for this game.</p>
                </div>
            </InnerContent>
        );
    }

    const pred = game.prediction;
    const away = game.awayTeam || {};
    const home = game.homeTeam || {};

    const addZeroes = num => { const n = Number(num); if (isNaN(n)) return '0.00'; return n.toFixed(2); };

    const awayScore = parseFloat(pred.awayScore || 0);
    const homeScore = parseFloat(pred.homeScore || 0);
    const awayWins = awayScore > homeScore;
    const homeWins = homeScore > awayScore;
    const confidence = Math.round((pred.confidence || 0) * 100);
    const stars = getStarRating(pred.confidence);
    const confColor = confidence >= 70 ? '#10b981' : confidence >= 50 ? '#f59e0b' : '#ef4444';

    const awayColor = readableColor(away.color || away.primaryColor);
    const homeColor = readableColor(home.color || home.primaryColor);

    const getPowerline = (team, isAway) => {
        const score = isAway ? awayScore : homeScore;
        const oppScore = isAway ? homeScore : awayScore;
        const ml = score >= oppScore ? pred.powerMoneyline?.favML : pred.powerMoneyline?.dogML;
        return formatOdd(ml, 'moneyline');
    };

    const renderTeamRow = (team, score, isWinner, side, color) => (
        <div style={{
            display: 'flex', alignItems: 'center', padding: '13px 16px',
            background: isWinner ? `${color}26` : 'rgba(255,255,255,0.02)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
            <div style={{ width: 4, height: 38, borderRadius: 3, background: color, marginRight: 12, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{side}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: 1 }}>
                    {team.logoUrl && <TeamLogoSmall src={team.logoUrl} alt="" onError={imageLoadError} style={{ width: 16, height: 16 }} />}
                    <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {shortName(team)}
                    </div>
                </div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 1 }}>
                    ML: <strong style={{ color: '#e2e8f0' }}>{getPowerline(team, side === 'Away')}</strong>
                </div>
            </div>
            {isWinner && (
                <span style={{
                    fontSize: '0.6rem', fontWeight: 800, color: '#e2e8f0', border: `1.5px solid ${color}`,
                    background: `${color}1f`, borderRadius: 20, padding: '2px 8px',
                    marginRight: 12, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap',
                }}>Win</span>
            )}
            <div style={{ fontSize: '2rem', fontWeight: 900, color: isWinner ? '#e2e8f0' : '#94a3b8', lineHeight: 1 }}>
                {addZeroes(score)}
            </div>
        </div>
    );

    return (
        <InnerContent fluid>
            {/* Score card */}
            <div style={{ borderRadius: 14, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', marginBottom: 10, background: 'rgba(255,255,255,0.03)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 16px', background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Predicted Final</span>
                    <div style={{ display: 'flex', gap: 1 }}>
                        {Array.from({ length: 5 }, (_, i) => (
                            <span key={i} style={{ color: i < stars ? '#f59e0b' : '#334155', fontSize: '0.85rem', lineHeight: 1 }}>
                                {i < stars ? '★' : '☆'}
                            </span>
                        ))}
                    </div>
                </div>
                {renderTeamRow(away, awayScore, awayWins, 'Away', awayColor)}
                {renderTeamRow(home, homeScore, homeWins, 'Home', homeColor)}
            </div>

            {/* Confidence bar */}
            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: '10px 14px', marginBottom: 10, border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Prediction PW Rating</span>
                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: confColor }}>{confidence}%</span>
                </div>
                <div style={{ height: 6, background: 'rgba(148,163,184,0.25)', borderRadius: 99 }}>
                    <div style={{ height: '100%', width: `${confidence}%`, background: `linear-gradient(90deg, ${confColor}88, ${confColor})`, borderRadius: 99, transition: 'width 0.6s ease' }} />
                </div>
            </div>

            {/* Model Insights (replaces advantages which are stripped from public API) */}
            <div style={{ borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', background: 'rgba(255,255,255,0.03)' }}>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '8px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Model Insights</span>
                </div>
                <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {/* Predicted winner */}
                    {(awayWins || homeWins) && (() => {
                        const winner = awayWins ? away : home;
                        const loser  = awayWins ? home : away;
                        const wColor = awayWins ? awayColor : homeColor;
                        const diff   = Math.abs(awayScore - homeScore).toFixed(1);
                        return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 8, background: `${wColor}1a`, borderLeft: `3px solid ${wColor}` }}>
                                <i className="fas fa-trophy" style={{ color: wColor, fontSize: '0.75rem' }} />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#e2e8f0' }}>{shortName(winner)} projected to win</div>
                                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2 }}>by {diff} points over {shortName(loser)}</div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Confidence insight */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 8, background: `${confColor}1a`, borderLeft: `3px solid ${confColor}` }}>
                        <i className="fas fa-chart-bar" style={{ color: confColor, fontSize: '0.75rem' }} />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#e2e8f0' }}>
                                {confidence >= 70 ? 'High PW Rating pick' : confidence >= 50 ? 'Moderate PW Rating' : 'Low PW Rating'}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2 }}>Model PW Rating: {confidence}%</div>
                        </div>
                    </div>

                    {/* Power moneyline */}
                    {pred.powerMoneyline?.favML != null && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 8, background: 'rgba(59,130,246,0.1)', borderLeft: '3px solid #3b82f6' }}>
                            <i className="fas fa-bolt" style={{ color: '#60a5fa', fontSize: '0.75rem' }} />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#e2e8f0' }}>Proven Line</div>
                                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2 }}>
                                    Fav: <strong style={{ color: '#60a5fa' }}>{formatOdd(pred.powerMoneyline.favML, 'moneyline')}</strong>
                                    {' · '}
                                    Dog: <strong style={{ color: '#f87171' }}>{formatOdd(pred.powerMoneyline.dogML, 'moneyline')}</strong>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </InnerContent>
    );
};

export default Prediction;
